import React, { useRef, useEffect } from 'react';
import InitialChoice from './InitialChoice';
import DesignCustomizer from './DesignCustomizer';
import ModalPageTwo from './ModalPageTwo';
import ModalPageThree from './ModalPageThree';

function Modal(props) {
  const modalRef = useRef(null);

  // Helper function to check if there are any designs
  const hasDesigns = () => {
    // Check for legacy design
    if (props.formData.design) return true;

    // Check for designs in customization
    if (props.formData.designCustomization) {
      // Check design pool
      if (
        props.formData.designCustomization.designPool &&
        props.formData.designCustomization.designPool.length > 0
      ) {
        return true;
      }

      // Check side designs
      if (props.formData.designCustomization.sideDesigns) {
        const {
          front = [],
          back = [],
          side = [],
        } = props.formData.designCustomization.sideDesigns;
        if (front.length > 0 || back.length > 0 || side.length > 0) {
          return true;
        }
      }
    }

    return false;
  };
  // Destructure props for useEffect dependencies
  const { isOpen, closeModal } = props;

  useEffect(() => {
    const handleClickOutside = event => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        // Give DesignCustomizer a moment to save state
        setTimeout(() => {
          closeModal();
        }, 50);
      }
    };

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        // Give DesignCustomizer a moment to save state
        setTimeout(() => {
          closeModal();
        }, 50);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, closeModal]);

  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.scrollTop = 0;
    }
  }, [props.modalStepNumber]);
  if (!props.isOpen) return null;

  function handleBack() {
    // If on step 2 and came from initial choice (own_artwork or contact_only), go back to step 0
    if (props.modalStepNumber === 2 && (props.formData.orderType === 'own_artwork' || props.formData.orderType === 'contact_only')) {
      props.setModalStepNumber(0);
    } else {
      props.setModalStepNumber(Math.max(props.modalStepNumber - 1, 0));
    }
  }

  // Determine modal class based on step
  const getModalClass = () => {
    const classes = ['wizard-modal'];

    if (props.modalStepNumber === 0) {
      classes.push('wizard-modal-with-initial-choice');
    } else if (props.modalStepNumber === 1 || (props.modalStepNumber === 2 && hasDesigns())) {
      classes.push('wizard-modal-with-customizer');
    } else if (props.modalStepNumber === 2 && !hasDesigns()) {
      classes.push('wizard-modal-with-page-two');
    } else if (props.modalStepNumber === 3) {
      classes.push('wizard-modal-with-page-three');
    }

    return classes.join(' ');
  };

  return (
    <div className="wizard-modal-wrapper">
      <div className={getModalClass()} ref={modalRef}>
        <button
          className="wizard-close-button"
          type="button"
          onClick={() => {
            // Give DesignCustomizer a moment to save state
            setTimeout(() => {
              props.closeModal();
            }, 50);
          }}
        >
          <span className="is-hidden-visually">Close</span>
        </button>
        {props.modalStepNumber === 0 && (
          <InitialChoice
            onSelectOption={props.handleInitialChoice}
            closeModal={props.closeModal}
            designGalleryUrl={props.designGalleryUrl}
          />
        )}
        {props.modalStepNumber === 1 && (
          <DesignCustomizer
            modalStepNumber={props.modalStepNumber}
            setModalStepNumber={props.setModalStepNumber}
            handleStartOver={props.handleStartOver}
            handleRemoveDesign={props.handleRemoveDesign}
            handleRemoveProduct={props.handleRemoveProduct}
            formData={props.formData}
            handleChange={props.handleChange}
            handleBack={handleBack}
            productGalleryUrl={props.productGalleryUrl}
            designGalleryUrl={props.designGalleryUrl}
          />
        )}
        {props.modalStepNumber === 2 && (
          <ModalPageTwo
            modalStepNumber={props.modalStepNumber}
            setModalStepNumber={props.setModalStepNumber}
            handleBack={handleBack}
            schools={props.schools}
            handleChange={props.handleChange}
            handleSubmitQuoteRequest={props.handleSubmitQuoteRequest}
            formData={props.formData}
            recaptchaKey={props.recaptchaKey}
          />
        )}
        {props.modalStepNumber === 3 && (
          <ModalPageThree
            formData={props.formData}
            modalStepNumber={props.modalStepNumber}
            setModalStepNumber={props.setModalStepNumber}
            previewUrl={props.previewUrl}
            artworkDisplayUrls={props.artworkDisplayUrls}
          />
        )}
      </div>
    </div>
  );
}

export default Modal;
